import {
  AutoNamingErrors,
  AutoNamingRulesForTaskShotQuery,
  AutoNamingRulesForTaskShotQueryVariables,
  DeliverySourceEnum,
  DeliveryTaskShotInput,
  UserTaskShot,
} from "@/__generated__/graphql";
import Error from "@/components/common/Error";
import {
  createDirectoryStructure,
  Directory,
  findAllFileNames,
} from "@/components/common/FileUpload/helpers";
import LongContentWrapper from "@/components/common/LongContentWrapper";
import ServiceDots, {
  getServiceOrSubserviceBasedOnSubservice,
} from "@/components/common/ServiceDots";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useUserContext } from "@/contexts/UserContextProvider";
import { getFileNameFromPath, isFile } from "@/helpers/filename";
import { formatShotWithSubServiceName } from "@/helpers/utils";
import { cn } from "@/lib/utils";
import { useQuery } from "@apollo/client";
import { compact, Dictionary, every, groupBy, uniq, uniqBy } from "lodash";
import { CircleAlert, RefreshCcw, X } from "lucide-react";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UploadDeliveryFormValues } from "@/components/Actions/Deliver/UploadDelivery";
import { getOrderedDeliveries } from "@/helpers/taskShotBid";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { formatDateTimeForDelivery } from "@/helpers/date";
import DragIcon from "@/assets/DragIcon";
import { AUTO_NAMING_RULES_QUERY } from "./LinkMedia";

type Props = {
  onNext: (deliveryKeysToTaskShotIds: DeliveryTaskShotInput[]) => void;
  taskShots: UserTaskShot[];
  uploadDeliveryFormValues?: UploadDeliveryFormValues;
  existingLinks?: DeliveryTaskShotInput[];
  setSubRuleSelection: (val: Dictionary<string>) => void;
  setDeliveryKeysToTaskShotIds: (val: DeliveryTaskShotInput[]) => void;
  isAutoDeliveringFiles?: boolean;
  deliveredFiles?: string[];
};

function LinkMediaUser(props: Props) {
  const { currentProject, isHotspringVendorProfile, currentUser } =
    useUserContext();
  const { source, files } = props.uploadDeliveryFormValues || {};
  const navigate = useNavigate();

  const [currentTaskShots, setCurrentTaskShots] = useState(props.taskShots);
  const [
    showCustomInstructionWarningModal,
    setShowCustomInstructionWarningModal,
  ] = useState(false);
  const [isScanningFiles, _] = useState<boolean>(false);
  const [subrule, setSubrule] = useState<Dictionary<string>>({});
  const [namingErrors] = useState<AutoNamingErrors[]>([]);
  const [errorMessage] = useState<string>("");

  const { data } = useQuery<
    AutoNamingRulesForTaskShotQuery,
    AutoNamingRulesForTaskShotQueryVariables
  >(AUTO_NAMING_RULES_QUERY, {
    variables: {
      taskShotIds: currentTaskShots.map((ts) => ts.id),
    },
    fetchPolicy: "network-only",
  });

  const requiredSubruleSelectionCount =
    data?.autoNamingRulesForTaskShot && !isHotspringVendorProfile
      ? data?.autoNamingRulesForTaskShot
          .filter((ts) => {
            if (ts?.serviceRules && ts?.serviceRules[0]?.subRuleNames?.length) {
              return ts?.serviceRules[0]?.subRuleNames?.length > 1;
            }
            return false;
          })
          .flatMap((cts) =>
            currentTaskShots
              .map((ts) => ts.taskShot)
              .filter((ts) => ts.task.service.id === cts?.task.service.id),
          ).length
      : 0;

  const customInstructionTaskShots = currentTaskShots
    .map((ts) => ts.taskShot)
    .filter(
      (taskShot) =>
        taskShot.task.folderStructureZipUrl || taskShot.task.specDocumentUrl,
    );
  const customInstructions = uniqBy(
    customInstructionTaskShots.map((taskShot) => ({
      service: taskShot.task.service.name,
      specDocUrl: taskShot.task.specDocumentUrl,
      folderStructureZipUrl: taskShot.task.folderStructureZipUrl,
    })),
    (customInstruction) => customInstruction.service,
  );

  const hasCustomInstructions =
    (currentUser?.office?.company.profile.isDefault ||
      currentProject?.sourceProject) &&
    customInstructions.length;

  const getInitialState = () => {
    let initialState;
    const deliveredFiles = props.isAutoDeliveringFiles
      ? compact(props.deliveredFiles || [])
      : files?.map((f: File) => f.webkitRelativePath || f.name) || [];
    const directoryStructure = createDirectoryStructure(deliveredFiles);
    const firstLevelElements = directoryStructure.map(
      (entry) => entry.absolutePath,
    );

    if (props.isAutoDeliveringFiles) {
      const newDeliveryKeysToTaskShotIds = currentTaskShots.map((ts) => {
        return getOrderedDeliveries(
          ts.taskShot.subcontractedDeliveries || [],
        )[0].deliveryFilesToSourceTaskShotIds.filter(
          (item) => item.taskShotId === ts.taskShot.id,
        );
      });
      const taskVersions = currentTaskShots.map((ts) => {
        return { taskShotId: ts.taskShot.id, version: 0 };
      });
      initialState = {
        deliveryKeysToTaskShotIds: newDeliveryKeysToTaskShotIds.flat(),
        additionalMediaKeys: [],
        taskShotDeliveryVersionMap: taskVersions,
        directoryStructure,
      };
    } else {
      initialState = {
        deliveryKeysToTaskShotIds: props.existingLinks || [],
        additionalMediaKeys:
          source === DeliverySourceEnum.Http
            ? firstLevelElements
            : files?.map((f) => f.name) || [],
        directoryStructure,
      };
    }

    return initialState;
  };
  const initialState = getInitialState();
  const [linkedMediaState, setLinkedMediaState] =
    useState<VendorDeliveryConfirmTableState>({
      ...initialState,
    });

  const { deliveryKeysToTaskShotIds, additionalMediaKeys } = linkedMediaState;
  const deliveries = groupBy(deliveryKeysToTaskShotIds, "taskShotId");

  const allDeliveriesLinked = every(
    currentTaskShots,
    (ts) => deliveries[ts.id],
  );

  const getTaskSelectedVersion = (taskShotId: string) => {
    return linkedMediaState.taskShotDeliveryVersionMap?.find(
      (item) => item.taskShotId === taskShotId,
    )?.version;
  };

  const setDeliveryVersion = (taskShotId: string, version: number) => {
    const {
      deliveryKeysToTaskShotIds: previousDeliveryKeysToTaskShotIds,
      taskShotDeliveryVersionMap,
    } = linkedMediaState;
    const taskShot = currentTaskShots.find(
      (ts) => ts.taskShot.id === taskShotId,
    )?.taskShot;

    const newDeliveryKeysToTaskShotIds =
      previousDeliveryKeysToTaskShotIds.filter(
        (item) => item.taskShotId !== taskShotId,
      );

    const newTaskVersion = taskShotDeliveryVersionMap?.filter(
      (item) => item.taskShotId !== taskShotId,
    );

    newTaskVersion?.push({ taskShotId, version });

    const orderedDeliveries = getOrderedDeliveries(
      taskShot?.subcontractedDeliveries || [],
    );

    if (taskShot && orderedDeliveries[version]) {
      newDeliveryKeysToTaskShotIds.push(
        ...orderedDeliveries[version].deliveryFilesToSourceTaskShotIds
          .map((item) => {
            return { ...item, version };
          })
          .filter((item) => item.taskShotId === taskShot.id),
      );
    }

    return setLinkedMediaState((prevState) => {
      return {
        ...prevState,
        deliveryKeysToTaskShotIds: newDeliveryKeysToTaskShotIds,
        taskShotDeliveryVersionMap: newTaskVersion,
      };
    });
  };

  const autoLinkMedia = () => {
    const used = new Set<string>();
    const newLinks: DeliveryTaskShotInput[] = [];

    currentTaskShots.forEach((ts) => {
      const shot = ts.taskShot.shot.name.toLowerCase();
      const match = additionalMediaKeys.find((k) =>
        k.toLowerCase().includes(shot),
      );
      if (match && !used.has(match)) {
        newLinks.push({ key: match, taskShotId: ts.id });
        used.add(match);
      }
    });

    if (!newLinks.length) {
      toast.info("No matches found");
      return;
    }

    setLinkedMediaState((s) => ({
      ...s,
      deliveryKeysToTaskShotIds: [...s.deliveryKeysToTaskShotIds, ...newLinks],
      additionalMediaKeys: s.additionalMediaKeys.filter((k) => !used.has(k)),
    }));

    toast.success(
      `Auto-linked ${newLinks.length} shot${newLinks.length > 1 ? "s" : ""}`,
    );
  };

  const goToNextStep = async () => {
    const expanded: DeliveryTaskShotInput[] = [];

    for (const link of deliveryKeysToTaskShotIds) {
      if (source === DeliverySourceEnum.Http) {
        const root = linkedMediaState.directoryStructure.find(
          (d: Directory) => d.absolutePath === link.key,
        );
        if (root) {
          findAllFileNames(root)
            .filter(isFile)
            .forEach((name) => expanded.push({ ...link, key: name }));
        } else {
          expanded.push(link);
        }
      } else {
        expanded.push(link);
      }
    }

    props.setDeliveryKeysToTaskShotIds(expanded);
    props.setSubRuleSelection(subrule);
    props.onNext(expanded);
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="p-6 flex bg-quinary">
        <div className="flex-3 pr-6">
          <Table className="table-fixed !border-spacing-x-6 !border-spacing-y-4 border-separate">
            <TableHeader className="text-base font-medium text-secondary-foreground sticky top-0 bg-transparent">
              <TableRow className="bg-transparent hover:bg-transparent border-none [&_th]:py-3 [&_th]:px-0">
                <TableHead className="">Shot Name</TableHead>
                <TableHead className="w-35">Service</TableHead>
                {!isHotspringVendorProfile &&
                  !!data?.autoNamingRulesForTaskShot
                    ?.filter((subRule) => !!subRule?.serviceRules?.length)
                    .filter(
                      (sb) =>
                        sb?.serviceRules &&
                        sb.serviceRules[0] &&
                        sb.serviceRules[0].subRuleNames &&
                        sb.serviceRules[0].subRuleNames?.length > 1,
                    )?.length && (
                    <TableHead className="w-35">Select Subrule</TableHead>
                  )}
                <TableHead className="">Linked Media</TableHead>
                {props.isAutoDeliveringFiles && (
                  <TableHead className="w-50">Vendor</TableHead>
                )}
              </TableRow>
            </TableHeader>

            <TableBody className="text-sm text-primary-foreground">
              {currentTaskShots.map((ts) => {
                const serviceName = ts.taskShot.task.service.name;
                const shotName = formatShotWithSubServiceName(
                  ts.taskShot.shot.name,
                  ts.taskShot.subService,
                );
                const deliveriesForTaskShot = (deliveries[ts.id] ||
                  []) as DeliveryTaskShotInput[];
                const subcontractedDeliveries = getOrderedDeliveries(
                  ts.taskShot.subcontractedDeliveries || [],
                );
                const vendorName =
                  subcontractedDeliveries.length &&
                  subcontractedDeliveries[0].vendor.profile.companyName;

                if (deliveriesForTaskShot.some((delivery) => delivery.isBroken))
                  return null;

                const serviceRuledata = data?.autoNamingRulesForTaskShot?.find(
                  (rule) => rule?.task.service.name === serviceName,
                );

                return (
                  <TableRow
                    className={cn(
                      "border-none bg-transparent hover:bg-transparent [&_td]:p-0",
                    )}
                    onDragOver={(e) => {
                      e.preventDefault();
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const key = e.dataTransfer.getData("text/plain");
                      if (additionalMediaKeys.includes(key)) {
                        setLinkedMediaState((s) => ({
                          ...s,
                          deliveryKeysToTaskShotIds: [
                            ...s.deliveryKeysToTaskShotIds.filter(
                              (l) => l.taskShotId !== ts.id,
                            ),
                            { key, taskShotId: ts.id },
                          ],
                          additionalMediaKeys: s.additionalMediaKeys.filter(
                            (k) => k !== key,
                          ),
                        }));
                      }
                    }}
                  >
                    <TableCell className="">
                      <div className="flex justify-between items-center">
                        <div className="border px-4 py-3 w-[85%] rounded-md">
                          <LongContentWrapper>{shotName}</LongContentWrapper>
                        </div>

                        <Button
                          className="border p-2!"
                          onClick={() => {
                            const newTsbs = currentTaskShots.filter(
                              (cts) => cts.id !== ts.id,
                            );
                            setCurrentTaskShots(newTsbs);
                          }}
                        >
                          <X />
                        </Button>
                      </div>
                    </TableCell>

                    <TableCell>
                      <ServiceDots
                        transparent
                        colorBorder
                        square
                        colorText
                        services={getServiceOrSubserviceBasedOnSubservice(
                          [ts.taskShot.task.service],
                          ts.taskShot.subService,
                        )}
                        innerClassName="w-full !py-2 text-[16px]"
                      />
                    </TableCell>

                    {!isHotspringVendorProfile &&
                      !!serviceRuledata?.serviceRules?.length &&
                      serviceRuledata?.serviceRules &&
                      serviceRuledata?.serviceRules[0]?.subRuleNames &&
                      serviceRuledata?.serviceRules[0]?.subRuleNames?.length >
                        1 && (
                        <TableCell>
                          <Select
                            onValueChange={(val) => {
                              setSubrule((prev) => {
                                const currentObj = { ...prev };
                                currentObj[ts.taskShot.id] = val;
                                return currentObj;
                              });
                            }}
                          >
                            <SelectTrigger className="w-30 border-none !bg-secondary">
                              <SelectValue placeholder="Select subrule" />
                            </SelectTrigger>

                            <SelectContent className="">
                              <SelectGroup>
                                {serviceRuledata?.serviceRules &&
                                  serviceRuledata.serviceRules[0]?.subRuleNames?.map(
                                    (subRuleName) => (
                                      <SelectItem
                                        value={subRuleName}
                                        key={subRuleName}
                                      >
                                        {subRuleName}
                                      </SelectItem>
                                    ),
                                  )}
                              </SelectGroup>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      )}

                    <TableCell>
                      <div className="max-h-50 overflow-y-scroll">
                        <div className="flex flex-col gap-5">
                          {!deliveriesForTaskShot.length && (
                            <div className="text-secondary-foreground text-xs font-light px-4 py-3 border rounded-md">
                              Drag media here to assign
                            </div>
                          )}

                          {deliveriesForTaskShot.map((delivery) => (
                            <div className="flex justify-between items-center">
                              <div className="border px-4 py-3 w-[85%] rounded-md">
                                <LongContentWrapper>
                                  {getFileNameFromPath(delivery.key)}
                                </LongContentWrapper>
                              </div>

                              <Button
                                className={cn(
                                  "border p-2!",
                                  props.isAutoDeliveringFiles && "hidden",
                                )}
                                onClick={() => {
                                  setLinkedMediaState({
                                    ...linkedMediaState,
                                    deliveryKeysToTaskShotIds:
                                      linkedMediaState.deliveryKeysToTaskShotIds.filter(
                                        (d) => d.key !== delivery.key,
                                      ),
                                    additionalMediaKeys: uniq([
                                      ...additionalMediaKeys,
                                      delivery.key,
                                    ]),
                                  });
                                }}
                              >
                                <X />
                              </Button>
                            </div>
                          ))}
                        </div>
                      </div>
                    </TableCell>

                    {props.isAutoDeliveringFiles &&
                      !!subcontractedDeliveries.length && (
                        <TableCell className="">
                          <div className="flex justify-between items-center">
                            <div className="border border-destructive text-destructive px-4 py-3 w-[85%] rounded-md">
                              <LongContentWrapper>
                                {vendorName}
                              </LongContentWrapper>
                            </div>

                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button className="border p-2! text-destructive mx-2">
                                  <CircleAlert />
                                </Button>
                              </TooltipTrigger>

                              <TooltipContent
                                className="p-6 border-2"
                                side="left"
                              >
                                <div className="text-base font-medium">
                                  {shotName}
                                </div>
                                <Separator className="my-4" />
                                <div className="text-sm font-medium text-center text-secondary-foreground my-4">
                                  This task has {subcontractedDeliveries.length}{" "}
                                  associated packages from {vendorName}
                                </div>

                                <div className="max-h-100 overflow-y-scroll">
                                  <div className="flex flex-col gap-2">
                                    {subcontractedDeliveries.map(
                                      (delivery, idx) => (
                                        <RadioGroup>
                                          <div
                                            className="p-4 bg-secondary rounded-md flex flex-col gap-2 cursor-pointer"
                                            onClick={() => {
                                              setDeliveryVersion(
                                                ts.taskShot.id,
                                                idx,
                                              );
                                            }}
                                          >
                                            <div className="flex items-center justify-between gap-6">
                                              <div className="flex flex-col gap-1 font-medium text-sm">
                                                {delivery.deliveryFilesToSourceTaskShotIds
                                                  .filter(
                                                    (v) =>
                                                      v.taskShotId ===
                                                      ts.taskShot.id,
                                                  )
                                                  .map((item) => (
                                                    <LongContentWrapper>
                                                      {getFileNameFromPath(
                                                        item.key,
                                                      )}
                                                    </LongContentWrapper>
                                                  ))}
                                              </div>

                                              <RadioGroupItem
                                                checked={
                                                  idx ===
                                                  getTaskSelectedVersion(
                                                    ts.taskShot.id,
                                                  )
                                                }
                                                value={idx.toString()}
                                              />
                                            </div>

                                            <div className="flex items-center justify-between gap-6 font-light text-xs">
                                              <div>
                                                <span>
                                                  Delivered on &nbsp;
                                                  {formatDateTimeForDelivery(
                                                    delivery.created,
                                                    delivery.creator.timezone,
                                                  )}{" "}
                                                  by &nbsp;
                                                  {vendorName}
                                                </span>
                                              </div>

                                              {idx === 0 && (
                                                <div className="px-4 py-1 bg-yellow-600 rounded-full font-bold">
                                                  Latest
                                                </div>
                                              )}
                                            </div>
                                          </div>
                                        </RadioGroup>
                                      ),
                                    )}
                                  </div>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </TableCell>
                      )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>

        {!props.isAutoDeliveringFiles && (
          <div className="flex-1 pl-6 border-l flex flex-col gap-5">
            <div className="flex justify-between items-start">
              <div>
                <div className="text-secondary-foreground font-medium">
                  Unlinked Media
                </div>
                <div className="text-secondary-foreground text-xs font-light mt-1">
                  Drag and drop media to assign to shot
                </div>
              </div>

              <div className="flex items-center gap-3">
                <Button
                  className="text-service-paint hover:bg-transparent bg-transparent hover:text-service-paint/80 p-0"
                  onClick={autoLinkMedia}
                >
                  <RefreshCcw />
                  Auto link
                </Button>

                <Button
                  className="text-link hover:bg-transparent bg-transparent hover:text-link/80 p-0"
                  onClick={() => {
                    setCurrentTaskShots(props.taskShots);
                    setLinkedMediaState(getInitialState());
                  }}
                >
                  Reset
                </Button>
              </div>
            </div>

            <div className="overflow-scroll max-h-80">
              <div className="flex flex-col gap-5">
                {linkedMediaState.additionalMediaKeys.sort().map((key) => {
                  return (
                    <div
                      className="border text-sm flex gap-2 items-center px-4 py-3 rounded-md max-w-100 cursor-grab hover:bg-secondary"
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/plain", key);
                        e.dataTransfer.dropEffect = "move";
                        setTimeout(() => {
                          const ele = e.target as HTMLElement;
                          ele.style.opacity = "0.3";
                        }, 0);
                      }}
                      onDragEnd={(e) => {
                        const ele = e.target as HTMLElement;
                        ele.style.opacity = "1";
                      }}
                    >
                      <DragIcon
                        color="var(--color-primary-foreground)"
                        className="shrink-0"
                      />
                      <LongContentWrapper>
                        {getFileNameFromPath(key)}
                      </LongContentWrapper>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="bg-primary border-t flex justify-end gap-4 py-4 px-6 items-center">
        {!!namingErrors.length && (
          <Error
            error={
              "We detected following issues in the delivery package. Please resolve the issues and redeliver the package"
            }
            errors={namingErrors
              .map(
                (e) =>
                  e.errors?.map((error) =>
                    error.concat(` for ${e.taskShotName}`),
                  ) || null,
              )
              .flat()}
          />
        )}

        <Error error={errorMessage} />

        <Button
          onClick={() => {
            navigate(-1);
          }}
        >
          Back
        </Button>

        <Button
          onClick={() => {
            goToNextStep();
          }}
          disabled={
            isScanningFiles ||
            !allDeliveriesLinked ||
            Object.keys(subrule).length !== requiredSubruleSelectionCount
          }
          className="bg-service-paint hover:bg-service-paint/96 text-white"
        >
          Next
        </Button>
      </div>

      {showCustomInstructionWarningModal && hasCustomInstructions && (
        <Dialog
          open
          onOpenChange={(open) =>
            !open && setShowCustomInstructionWarningModal(false)
          }
        >
          <DialogContent>
            <div className="font-medium text-lg">Warning!</div>
            <Separator />
            <div>
              You are delivering tasks that have custom Instructions. Please
              ensure you have followed them. Spec Documents:
            </div>
            {customInstructions.map((instruction) => {
              return (
                <div className="text-sm [&_a]:text-service-paint">
                  <p className="font-medium">{instruction.service} :</p>
                  <div>
                    {instruction.specDocUrl && (
                      <a href={instruction.specDocUrl}>
                        Specification Document
                      </a>
                    )}
                    {instruction.folderStructureZipUrl && (
                      <a href={instruction.folderStructureZipUrl}>
                        Delivery Folder Structure
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
            <Separator />
            <Button
              className="bg-service-paint hover:bg-service-paint/96 text-white"
              onClick={() => {
                if (hasCustomInstructions)
                  setShowCustomInstructionWarningModal(true);
                else goToNextStep();
              }}
            >
              Confirm
            </Button>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

interface VendorDeliveryConfirmTableState {
  deliveryKeysToTaskShotIds: DeliveryTaskShotInput[];
  additionalMediaKeys: string[];
  taskShotDeliveryVersionMap?: TaskShotDeliveryVersion[];
  directoryStructure: Directory[];
}

interface TaskShotDeliveryVersion {
  taskShotId: string;
  version: number;
}

export default LinkMediaUser;
